import { useTranslation } from 'react-i18next';
import { Popover } from '@amzn/storm-ui';
import { ROLE_ENUM_TO_LABEL_MAP } from '../../../../constants/userRoles';
import { UserProfileApiResponse } from '../../../../interfaces/user/UserInterfaces';
import { getFormattedDate } from '../../../../utils/helpers';
import DisplayProfileHeader from '../DisplayProfileHeader/DisplayProfileHeader';
import {
  FormattedText,
  ProfileDetailsFlexCard,
  ProfileIcon,
  MemberSinceText,
} from './UserProfileDetailsSection.styles';

interface UserProfileDetailsSectionProps {
  isOpen: boolean;
  handleProfileDisplay: () => void;
  handleClose: () => void;
  userProfileData?: UserProfileApiResponse;
}

const UserProfileDetailsSection = ({
  userProfileData,
  isOpen,
  handleProfileDisplay,
  handleClose,
}: UserProfileDetailsSectionProps) => {
  const { t } = useTranslation();

  if (!userProfileData?.user) return null;

  const { firstName, lastName, userRole, createdAt } = userProfileData.user;
  const userDisplayName = `${firstName} ${lastName}`;
  const roleLabel =
    ROLE_ENUM_TO_LABEL_MAP.find((option) => option.role === userRole)?.label ??
    'user-role-form-label';
  const userDetails = `${userDisplayName} - ${t(roleLabel)}`;
  const memberSinceDate = getFormattedDate(createdAt);
  const nameInitials = `${firstName?.charAt(0)}${lastName?.charAt(0)}`;

  return (
    <Popover
      id="profile-details-popover"
      isOpen={isOpen}
      trigger={
        <DisplayProfileHeader
          isOpen={isOpen}
          nameInitials={nameInitials}
          userDisplayName={userDisplayName}
          handleProfileDisplay={handleProfileDisplay}
        />
      }
      onCloseButtonClick={handleClose}
      withCloseButton={false}
      align="end"
      variant="default"
      position="bottom"
      autoFocus
    >
      <ProfileDetailsFlexCard id="profile-details-inner-card">
        <ProfileIcon id="profile-popover-profile-image">
          {nameInitials}
        </ProfileIcon>
        <FormattedText id="user-details">{userDetails}</FormattedText>
        {!!memberSinceDate && (
          <MemberSinceText id="user-start-date-text">
            {t('profile-section-member-since-text', {
              date: memberSinceDate,
            })}
          </MemberSinceText>
        )}
      </ProfileDetailsFlexCard>
    </Popover>
  );
};

export default UserProfileDetailsSection;
